import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Vault } from "lucide-react";
import { API } from "@/apiConfig";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

export function CashDropDialog({ open, onOpenChange, onComplete }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    const stationId = localStorage.getItem("station_id") || "default";
    axios.get(`${API}/reports/cash-drop-status?station_id=${stationId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [open]);

  const handleSubmit = async () => {
    const dropAmount = parseFloat(amount);
    if (isNaN(dropAmount) || dropAmount <= 0) { toast.error("Please enter a valid amount"); return; }
    const token = localStorage.getItem("token");
    const stationId = localStorage.getItem("station_id") || "default";
    setLoading(true);
    try {
      await axios.post(`${API}/reports/cash-drop?station_id=${stationId}`, { amount: dropAmount, note }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Cash drop of $${dropAmount.toFixed(2)} recorded`);
      setAmount(""); setNote("");
      onComplete && onComplete();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record cash drop");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cash-drop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Vault className="w-5 h-5 text-orange-600" /> Cash Drop
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {status && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="cash-drop-drawer-summary">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-zinc-500">Cash in drawer now</span>
                <span className="text-2xl font-black text-emerald-700 font-mono" data-testid="cash-drop-in-drawer">{money(status.cash_in_drawer)}</span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs text-zinc-600">
                <span>Starting cash</span><span className="text-right font-mono" data-testid="cash-drop-starting">{money(status.starting_cash)}</span>
                <span>+ Cash sales</span><span className="text-right font-mono" data-testid="cash-drop-cash-sales">{money(status.cash_sales)}</span>
                {status.vendor_payouts > 0 && (<><span>− Vendor payouts</span><span className="text-right font-mono">−{money(status.vendor_payouts)}</span></>)}
                <span>− Previous drops ({status.drops?.length || 0})</span><span className="text-right font-mono text-orange-700" data-testid="cash-drop-previous-total">−{money(status.cash_drops)}</span>
              </div>
              {status.drops?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-200 max-h-24 overflow-y-auto space-y-0.5" data-testid="cash-drop-previous-list">
                  {status.drops.map((d) => (
                    <div key={d.id} className="flex justify-between text-[11px] text-zinc-600">
                      <span>{fmtTime(d.created_at)} · {d.employee_name || "—"}{d.note ? ` · ${d.note}` : ""}</span>
                      <span className="font-mono">{money(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="cash-drop-amount">Drop Amount</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <Input
                id="cash-drop-amount" type="text" inputMode="decimal" autoFocus value={amount}
                onChange={(e) => { if (e.target.value === "" || /^\d*\.?\d{0,2}$/.test(e.target.value)) setAmount(e.target.value); }}
                onBlur={() => { if (amount && !isNaN(parseFloat(amount))) setAmount(parseFloat(amount).toFixed(2)); }}
                placeholder="0.00" className="pl-7 text-lg font-semibold" data-testid="cash-drop-amount-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="cash-drop-note">Note (optional)</Label>
            <Textarea id="cash-drop-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. dropped into safe" className="mt-1" rows={2} data-testid="cash-drop-note-input" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="cash-drop-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading || !amount}
              className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="cash-drop-submit">
              <Vault className="w-4 h-4 mr-1" /> Record Drop
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
